import { HOSTS, ENDPOINTS } from "@/constants";
import { type AxiosResponse, httpClient } from "@/services/httpClient";
import type { FormulaState, RootState, ExpressionTestResult } from "@/types";
import type { ActionTree } from "vuex";
import {
  CHECK_FORMULA,
  CHECK_FORMULA_FAILURE,
  CHECK_FORMULA_SUCCESS,
  LOAD_FORMULA_VARIABLES,
  LOAD_FORMULA_VARIABLES_FAILURE,
  LOAD_FORMULA_VARIABLES_SUCCESS,
  RESET_FORMULA,
  SAVE_FORMULA,
  SAVE_FORMULA_FAILURE,
  SAVE_FORMULA_SUCCESS,
  LOAD_FORMULA,
  LOAD_FORMULA_SUCCESS,
  LOAD_FORMULA_FAILURE,
  RESET_TO_EDIT_FORMULA,
  INIT_STORE_FORMULA_FAILURE,
} from "./mutations";
import router from "@/router";

export const actions: ActionTree<FormulaState, RootState> = {
  fetchVariables({ commit }, params: any) {
    commit(LOAD_FORMULA_VARIABLES);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.VARIABLES}`, {
        params: {
          clientId: params.clientId,
          placeId: params.placeId,
          projectId: params.projectId,
          limit: params.itemPerPage,
          offset: params.offset,
          name: params.name,
          unitName: params.unitName,
          sourceTechnicalName: params.sourceTechnicalName,
          projectName: params.projectName,
          type: params.type,
        },
      })
      .then(
        (response: any) => {
          commit(LOAD_FORMULA_VARIABLES_SUCCESS, {
            variables: response.data.map((variable: any) => {
              return { ...variable, aggregationId: variable.aggregation.id };
            }),
            count: response.total_count,
          });
        },
        (error) => {
          commit(LOAD_FORMULA_VARIABLES_FAILURE, error.message);
        },
      );
  },

  // CHECK FORMULA
  checkFormula({ commit }, formulaTest: any) {
    commit(CHECK_FORMULA);
    httpClient
      .post(`${HOSTS.AWS}/${ENDPOINTS.CHECK_FORMULA}`, formulaTest)
      .then(
        (response: AxiosResponse<ExpressionTestResult>) => {
          commit(CHECK_FORMULA_SUCCESS, {
            data: formulaTest,
            result:
              formulaTest.start === null
                ? response.data.results[0]
                : response.data.results,
          });
        },
        (error) => {
          commit(CHECK_FORMULA_FAILURE, error.message);
        },
      );
  },

  // SAVE FORMULA
  saveFormula({ commit }, formula: any) {
    commit(SAVE_FORMULA);
    httpClient
      .post(`${HOSTS.AWS}/${ENDPOINTS.FORMULA}`, {
        ...formula,
        gobackInfos: undefined,
        graphId: undefined,
        dashboardId: undefined,
      })
      .then(
        (response: any) => {
          if (response) {
            commit(SAVE_FORMULA_SUCCESS, response.data);
            if (formula.gobackInfos) {
              if (formula.gobackInfos.name) {
                router.push({
                  name: formula.gobackInfos.name,
                });
              }
              if (formula.gobackInfos.path) {
                router.push({
                  path: formula.gobackInfos.path,
                });
              }
            }
            if (formula.gobackInfos === null) {
              const projectId = formula?.project?.id;
              const formulaId = response.data?.id;
              const graphId = formula?.graphId;
              const dashboardId = formula?.dashboardId;
              router.push({
                name: "editFormula",
                params: {
                  projectId: projectId,
                  formulaId: formulaId,
                },
                query: {
                  graphId: graphId ?? undefined,
                  dashboardId: dashboardId ?? undefined,
                },
              });
            }
          } else {
            commit(SAVE_FORMULA_FAILURE, "error");
          }
        },
        (error) => {
          commit(SAVE_FORMULA_FAILURE, error.message);
        },
      );
  },

  // LOAD FORMULA
  loadFormula({ commit }, formulaId: string) {
    commit(LOAD_FORMULA);
    httpClient.get(`${HOSTS.AWS}/${ENDPOINTS.FORMULA}/${formulaId}`).then(
      (response: any) => {
        commit(LOAD_FORMULA_SUCCESS, response.data);
      },
      (error) => {
        commit(LOAD_FORMULA_FAILURE, error.message);
      },
    );
  },

  updateFormula({ commit }, formula: any) {
    commit(SAVE_FORMULA);
    httpClient
      .put(`${HOSTS.AWS}/${ENDPOINTS.FORMULA}/${formula.id}`, {
        ...formula,
        id: undefined,
        gobackInfos: undefined,
      })
      .then(
        (response: any) => {
          commit(SAVE_FORMULA_SUCCESS, response.data);
          if (formula.gobackInfos) {
            if (formula.gobackInfos.name) {
              router.push({
                name: formula.gobackInfos.name,
              });
            }
            if (formula.gobackInfos.path) {
              router.push({
                path: formula.gobackInfos.path,
              });
            }
          }
        },
        (error) => {
          commit(SAVE_FORMULA_FAILURE, error.message);
        },
      );
  },

  checkFormulaError({ commit }, error: string) {
    commit(CHECK_FORMULA_FAILURE, error);
  },

  resetCurrentFormula({ commit }) {
    commit(RESET_FORMULA);
  },

  resetNewFormula({ commit }) {
    commit(RESET_FORMULA);
  },

  resetToEditFormula({ commit }) {
    commit(RESET_TO_EDIT_FORMULA);
  },

  initStoreFormula({ commit }) {
    commit(INIT_STORE_FORMULA_FAILURE);
  },
};
